import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Search, EyeOff, Eye } from "lucide-react";
import ModerationTab from "./ModerationTab";
import {
  SubTabs, Pill, EmptyState, Loading, ErrorState, TableShell, Th,
  Pagination, ConfirmModal,
} from "./AdminUI";

const TABS = [{ key: "content", label: "Content" }, { key: "moderation", label: "Moderation Queue" }];

export default function CommunityTab({ initialTab }) {
  const [tab, setTab] = useState(initialTab === "moderation" ? "moderation" : "content");
  useEffect(() => { if (initialTab) setTab(initialTab); }, [initialTab]);
  return (
    <div data-testid="admin-community-tab">
      <SubTabs tabs={TABS} active={tab} onChange={setTab} testidPrefix="community-tab" />
      {tab === "content" ? <ContentBrowser /> : <ModerationTab />}
    </div>
  );
}

const KINDS = [
  ["wall", "Wall"], ["pro_post", "Pro posts"], ["recommendation", "Recommendations"],
  ["project", "Projects"], ["request", "Requests"], ["review", "Reviews"],
];
const PERIODS = [["", "Any time"], ["7", "7 days"], ["30", "30 days"], ["90", "90 days"]];

function ContentBrowser() {
  const [kind, setKind] = useState("wall");
  const [q, setQ] = useState("");
  const [days, setDays] = useState("30");
  const [reported, setReported] = useState(false);
  const [hidden, setHidden] = useState("");
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [modal, setModal] = useState(null);

  const load = useCallback(() => {
    setError(null);
    const params = { kind, page, page_size: 25 };
    if (q.trim()) params.q = q.trim();
    if (days) params.days = Number(days);
    if (reported) params.reported = true;
    if (hidden) params.hidden = hidden === "hidden";
    api.get("/admin/community/content", { params })
      .then(r => setData(r.data)).catch(e => setError(e.response?.data?.detail || e.message));
  }, [kind, q, days, reported, hidden, page]);

  useEffect(() => { setPage(1); }, [kind, days, reported, hidden]);
  useEffect(() => { const t = setTimeout(load, q ? 300 : 0); return () => clearTimeout(t); }, [load, q]);

  const toggleHide = async (item, reason) => {
    const base = item.kind === "review" ? "reviews" : "posts";
    const path = item.hidden ? "unhide" : "hide";
    try {
      await api.post(`/admin/${base}/${item.id}/${path}`, item.hidden ? {} : { reason });
      toast.success(item.hidden ? "Content restored" : "Content hidden — logged in the audit trail");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <div>
      <p className="text-xs text-[#222]/50 mb-3">
        Public and community content only. Direct messages, My Board notes and private Project material are never listed here.
      </p>
      <div className="flex flex-wrap items-center gap-2 mb-4">
        {KINDS.map(([k, l]) => (
          <button key={k} onClick={() => setKind(k)}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold ${kind === k ? "bg-[#222] text-white" : "bg-white border border-black/10"}`}
            data-testid={`content-kind-${k}`}>{l}</button>
        ))}
      </div>
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <div className="flex items-center gap-2 bg-white border border-black/10 rounded-full px-3 py-2 flex-1 min-w-[200px] max-w-xs">
          <Search size={14} className="text-[#222]/40" />
          <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search text…"
            className="flex-1 outline-none text-sm bg-transparent min-w-0" data-testid="content-search" />
        </div>
        <select value={days} onChange={e => setDays(e.target.value)}
          className="px-3 py-2 rounded-full text-xs font-semibold bg-white border border-black/10 outline-none"
          data-testid="content-period">
          {PERIODS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        <select value={hidden} onChange={e => setHidden(e.target.value)}
          className="px-3 py-2 rounded-full text-xs font-semibold bg-white border border-black/10 outline-none"
          data-testid="content-visibility">
          <option value="">All visibility</option>
          <option value="visible">Visible only</option>
          <option value="hidden">Hidden only</option>
        </select>
        <button onClick={() => setReported(v => !v)}
          className={`px-3 py-2 rounded-full text-xs font-semibold ${reported ? "bg-red-600 text-white" : "bg-white border border-black/10"}`}
          data-testid="content-reported">Reported only</button>
      </div>

      {error ? <ErrorState error={error} onRetry={load} />
        : !data ? <Loading />
        : data.items.length === 0 ? (
          <EmptyState title="Nothing matches" body="No content of this type matches the active filters."
            testid="content-empty" />
        ) : (
          <>
            <TableShell testid="content-table">
              <thead>
                <tr>
                  <Th>Content</Th>
                  <Th className="hidden md:table-cell">Author</Th>
                  <Th>Status</Th>
                  <Th className="hidden lg:table-cell">Date</Th>
                  <Th className="text-right">Action</Th>
                </tr>
              </thead>
              <tbody>
                {data.items.map(it => (
                  <tr key={it.id} className="border-t border-black/5 align-top" data-testid={`content-row-${it.id}`}>
                    <td className="px-4 py-3 max-w-[380px]">
                      {it.title && <p className="font-medium text-sm truncate">{it.title}</p>}
                      <p className="text-xs text-[#222]/60 line-clamp-2">{it.excerpt || "—"}</p>
                      {it.category && <span className="text-[10px] text-[#222]/40">{it.category}</span>}
                    </td>
                    <td className="px-4 py-3 hidden md:table-cell text-xs">
                      <p className="truncate max-w-[140px]">{it.author.name || "—"}</p>
                      <span className="text-[#222]/40">{it.author.type}</span>
                    </td>
                    <td className="px-4 py-3 space-x-1 whitespace-nowrap">
                      {it.hidden ? <Pill tone="red">Hidden</Pill> : <Pill tone="green">Live</Pill>}
                      {it.report_count > 0 && <Pill tone="amber">{it.report_count} report{it.report_count > 1 ? "s" : ""}</Pill>}
                      {it.status && <Pill tone="neutral">{it.status}</Pill>}
                    </td>
                    <td className="px-4 py-3 text-xs text-[#222]/50 hidden lg:table-cell whitespace-nowrap">
                      {it.created_at ? new Date(it.created_at).toLocaleDateString() : "—"}
                    </td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      {(it.kind === "review" || it.kind !== "request") && (
                        it.hidden ? (
                          <button onClick={() => toggleHide(it)}
                            className="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-full bg-[#2E6B57] text-white font-semibold"
                            data-testid={`content-unhide-${it.id}`}><Eye size={12} /> Restore</button>
                        ) : (
                          <button onClick={() => setModal(it)}
                            className="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-full bg-black/5 font-semibold"
                            data-testid={`content-hide-${it.id}`}><EyeOff size={12} /> Hide</button>
                        )
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </TableShell>
            <Pagination page={data.page} pageSize={data.page_size} total={data.total} onPage={setPage}
              testid="content-pagination" />
          </>
        )}

      {modal && (
        <ConfirmModal title="Hide this content" danger
          impact={"The content stops appearing on the Wall, Feed, profiles and search for everyone. "
            + "It is NOT deleted — the author's copy and all engagement remain, and you can restore it at any time. "
            + "This action is written to the audit log."}
          confirmLabel="Hide content"
          onConfirm={r => toggleHide(modal, r)} onClose={() => setModal(null)} />
      )}
    </div>
  );
}
